import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Medal, Users, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import { resolveAthletePhoto } from "@/lib/photoFallback";

/**
 * V11.12 — Palmarès agrégé du coach.
 *
 * Récupère `/api/coaches/{id}/palmares` et affiche :
 *   • KPI globaux (nb athlètes coachés, or/argent/bronze/total)
 *   • Top athlètes (grille 12 max, tri par médailles)
 *   • Top disciplines
 *   • Compétitions phares
 *   • Derniers palmarès (feed chronologique)
 *
 * Tout est calculé côté backend à partir des `Athlete.achievements` des
 * athlètes liés au coach (via `athletes_ids` OU via `entity_links` acceptés
 * avec `role="athlete_coached_by"`).
 */
export function CoachPalmares({ coachId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!coachId) return () => {};
    setLoading(true);
    setErr(false);
    api.get(`/coaches/${coachId}/palmares`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErr(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [coachId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <Card className="p-5 text-sm text-muted-foreground italic" data-testid="coach-palmares-error">
        Impossible de charger le palmarès agrégé pour le moment.
      </Card>
    );
  }

  const { athletes_count, medals, by_discipline, top_competitions, top_athletes, recent_achievements } = data;

  if (!athletes_count) {
    return (
      <Card className="p-6 text-center space-y-2" data-testid="coach-palmares-empty">
        <Users className="h-8 w-8 mx-auto text-muted-foreground" />
        <h3 className="font-display font-semibold text-sm">Pas encore d'athlètes rattaché·e·s</h3>
        <p className="text-xs text-muted-foreground max-w-md mx-auto">
          Le palmarès agrégé apparaîtra automatiquement dès que des athlètes déclareront ce coach dans leur fiche
          (et que le lien sera validé).
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-palmares">
      {/* ================ KPI cards ================ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="coach-palmares-kpis">
        <KpiCard icon={Users} label="Athlètes coachés" value={athletes_count} tone="lagoon" testid="coach-palmares-kpi-athletes" />
        <KpiCard icon={MedalIcon("or")}     label="Or"     value={medals.or}     tone="gold"   testid="coach-palmares-kpi-or" />
        <KpiCard icon={MedalIcon("argent")} label="Argent" value={medals.argent} tone="silver" testid="coach-palmares-kpi-argent" />
        <KpiCard icon={MedalIcon("bronze")} label="Bronze" value={medals.bronze} tone="bronze" testid="coach-palmares-kpi-bronze" />
      </div>

      {/* ================ Top athlètes ================ */}
      {top_athletes.length > 0 && (
        <section data-testid="coach-palmares-top-athletes">
          <SectionTitle icon={Sparkles} title="Athlètes coachés (top médailles)" hint={`${top_athletes.length} athlètes`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {top_athletes.map((a) => <TopAthleteRow key={a.id} a={a} />)}
          </div>
        </section>
      )}

      {/* ================ By discipline & top competitions ================ */}
      <div className="grid md:grid-cols-2 gap-4">
        {by_discipline.length > 0 && (
          <section data-testid="coach-palmares-by-discipline">
            <SectionTitle icon={TrendingUp} title="Disciplines" />
            <Card className="p-4">
              <ul className="space-y-2">
                {by_discipline.slice(0, 8).map((d) => (
                  <li key={d.discipline} className="flex items-center justify-between gap-3 text-sm">
                    <span className="capitalize truncate min-w-0">{d.discipline}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {d.or > 0 && <MedalPill tone="or" count={d.or} />}
                      {d.argent > 0 && <MedalPill tone="argent" count={d.argent} />}
                      {d.bronze > 0 && <MedalPill tone="bronze" count={d.bronze} />}
                      <span className="font-mono text-xs text-muted-foreground">·&nbsp;{d.count}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        )}

        {top_competitions.length > 0 && (
          <section data-testid="coach-palmares-top-competitions">
            <SectionTitle icon={Trophy} title="Compétitions phares" />
            <Card className="p-4">
              <ul className="space-y-2">
                {top_competitions.map((c) => (
                  <li key={c.competition} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate min-w-0">{c.competition}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {c.or > 0 && <MedalPill tone="or" count={c.or} />}
                      {c.argent > 0 && <MedalPill tone="argent" count={c.argent} />}
                      {c.bronze > 0 && <MedalPill tone="bronze" count={c.bronze} />}
                      <span className="font-mono text-xs text-muted-foreground">·&nbsp;{c.count}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        )}
      </div>

      {/* ================ Feed derniers palmarès ================ */}
      {recent_achievements.length > 0 && (
        <section data-testid="coach-palmares-recent">
          <SectionTitle icon={Medal} title="Derniers palmarès" hint={`${recent_achievements.length} récents`} />
          <Card className="divide-y">
            {recent_achievements.slice(0, 12).map((r, idx) => <RecentRow key={`${r.athlete_id}-${idx}`} r={r} />)}
          </Card>
        </section>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------
function SectionTitle({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-display font-semibold text-sm flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        {title}
      </h3>
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "lagoon", testid }) {
  const toneCls = {
    lagoon: "text-[hsl(var(--aito-lagoon))]",
    gold:   "text-[hsl(var(--aito-gold))]",
    silver: "text-slate-400",
    bronze: "text-orange-500",
  }[tone];
  return (
    <Card className="p-4" data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wide ${toneCls}`}>
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 font-mono text-2xl font-bold">{value}</div>
    </Card>
  );
}

function MedalIcon(tone) {
  const toneCls =
    tone === "or" ? "text-[hsl(var(--aito-gold))]" :
    tone === "argent" ? "text-slate-400" :
    "text-orange-500";
  const Comp = (props) => <Medal {...props} className={`${props.className || "h-4 w-4"} ${toneCls}`} />;
  Comp.displayName = `MedalIcon(${tone})`;
  return Comp;
}

function MedalPill({ tone, count }) {
  const cls = {
    or:     "bg-[hsl(var(--aito-gold))] text-black",
    argent: "bg-slate-300 text-slate-900",
    bronze: "bg-orange-400 text-orange-950",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full text-[10px] font-mono font-semibold px-1.5 py-0.5 ${cls}`} data-testid={`medal-pill-${tone}`}>
      <Medal className="h-2.5 w-2.5" />{count}
    </span>
  );
}

function TopAthleteRow({ a }) {
  const photoUrl = useMemo(() => resolveAthletePhoto(a), [a]);
  return (
    <Link
      to={`/athletes/${a.slug || a.id}`}
      className="group block"
      data-testid={`coach-palmares-athlete-${a.id}`}
    >
      <Card className="p-3 flex items-center gap-3 aito-card-hover h-full">
        <div className="h-12 w-12 shrink-0 rounded-full overflow-hidden bg-muted ring-2 ring-[hsl(var(--aito-lagoon))]/30">
          <img
            src={photoUrl}
            alt={a.full_name}
            className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.src = "/branding/athlete-male.png"; }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm truncate group-hover:text-[hsl(var(--aito-lagoon))]">{a.full_name}</div>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {a.medals.or > 0 && <MedalPill tone="or" count={a.medals.or} />}
            {a.medals.argent > 0 && <MedalPill tone="argent" count={a.medals.argent} />}
            {a.medals.bronze > 0 && <MedalPill tone="bronze" count={a.medals.bronze} />}
            {a.medals.total === 0 && a.achievements_count > 0 && (
              <Badge variant="outline" className="text-[10px] font-mono">{a.achievements_count} entrées</Badge>
            )}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:text-[hsl(var(--aito-lagoon))] transition-colors" />
      </Card>
    </Link>
  );
}

function RecentRow({ r }) {
  const dateLabel = r.year ? (r.month ? `${String(r.month).padStart(2, "0")}/${r.year}` : `${r.year}`) : "";
  return (
    <div className="p-3 flex items-start gap-3 min-w-0" data-testid="coach-palmares-recent-row">
      <div className="flex flex-col items-center gap-0.5 shrink-0 min-w-[54px]">
        {r.medal ? (
          <Medal className={`h-5 w-5 ${
            r.medal === "or" ? "text-[hsl(var(--aito-gold))]" :
            r.medal === "argent" ? "text-slate-400" :
            "text-orange-500"
          }`} />
        ) : (
          <Trophy className="h-5 w-5 text-muted-foreground/50" />
        )}
        {dateLabel && <span className="font-mono text-[10px] text-muted-foreground">{dateLabel}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{r.title || r.competition || "Résultat"}</div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1 flex-wrap">
          <Link to={`/athletes/${r.athlete_slug || r.athlete_id}`} className="hover:underline hover:text-[hsl(var(--aito-lagoon))] font-medium">
            {r.athlete_name}
          </Link>
          {r.discipline && <span>· <span className="capitalize">{r.discipline}</span></span>}
          {r.level && <span>· {r.level}</span>}
          {r.place && <span>· {r.place}</span>}
        </div>
      </div>
    </div>
  );
}
